import { createContext, useCallback, useContext, useEffect, useState } from "react";

/*
 * A new key, not "lfit-theme". The old key was written on every visit, so it
 * holds "dark" for everyone who came while dark was the default, chosen or
 * not. This one is written only when the reader presses the toggle.
 */
const STORAGE_KEY = "vm-theme";
const ThemeContext = createContext(null);

/**
 * Light is the default: an owner reads this on a phone at the front desk under
 * tube lights or in daylight, and the light page is the one that reads there.
 * Someone who picks dark keeps it. Mirrors the bootstrap script in
 * public/index.html, which applies the same choice before first paint.
 */
function initialTheme() {
  if (typeof window === "undefined") return "light";

  try {
    return window.localStorage?.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Saved only here, on a deliberate choice, so the default can change again
  // later without every past visitor being stuck on the old one.
  const toggle = useCallback(
    () =>
      setTheme((t) => {
        const next = t === "dark" ? "light" : "dark";
        try {
          window.localStorage?.setItem(STORAGE_KEY, next);
        } catch {
          // Private browsing denies localStorage. The theme still applies.
        }
        return next;
      }),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) {
    // Rendered outside the provider — default rather than crash, so a component
    // can be tested in isolation.
    return { theme: "light", setTheme: () => {}, toggle: () => {} };
  }
  return value;
}
